// 问卷API - 使用RTK Query
// 基于后端Motia Framework API设计
// 直接调用真实API端点

import { apiSlice } from './apiSlice';
import {
  InitialScreeningQuestionnaire,
  QuestionnaireRequest,
  QuestionnaireFilters,
} from '../../types/questionnaire';
import { User } from '../slices/authSlice';

// 问卷相关API端点
export const questionnaireApi = apiSlice.injectEndpoints({
  endpoints: builder => ({
    // 创建问卷
    createQuestionnaire: builder.mutation<
      {
        success: boolean;
        data: { questionnaire: InitialScreeningQuestionnaire; message: string };
      },
      QuestionnaireRequest
    >({
      query: questionnaireData => ({
        url: '/questionnaire/create',
        method: 'POST',
        body: questionnaireData,
      }),
      invalidatesTags: ['Questionnaire'],
    }),

    // 保存问卷草稿
    saveQuestionnaireDraft: builder.mutation<
      {
        success: boolean;
        data: { questionnaire: InitialScreeningQuestionnaire; message: string };
      },
      { id?: string; data: Partial<QuestionnaireRequest> }
    >({
      query: payload => ({
        url: '/questionnaire/draft',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Questionnaire'],
    }),

    // 更新问卷
    updateQuestionnaire: builder.mutation<
      {
        success: boolean;
        data: { questionnaire: InitialScreeningQuestionnaire; message: string };
      },
      { id: string; data: Partial<QuestionnaireRequest> }
    >({
      query: ({ id, data }) => ({
        url: `/questionnaire/${id}/update`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Questionnaire', id },
        { type: 'Questionnaire', id: 'LIST' },
      ],
    }),

    // 提交问卷
    submitQuestionnaire: builder.mutation<
      {
        success: boolean;
        data: { questionnaire: InitialScreeningQuestionnaire; message: string };
      },
      string
    >({
      query: id => ({
        url: `/questionnaire/${id}/submit`,
        method: 'POST',
      }),
      // 提交问卷会影响统计数据（今日接收、总接收等）
      invalidatesTags: ['Questionnaire'],
    }),

    // 撤回问卷（发行人在审核开始前可撤回）
    withdrawQuestionnaire: builder.mutation<
      {
        success: boolean;
        data: { questionnaire: InitialScreeningQuestionnaire; message: string };
      },
      { id: string; reason?: string }
    >({
      query: ({ id, reason }) => ({
        url: `/questionnaire/${id}/withdraw`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Questionnaire', id },
        { type: 'Questionnaire', id: 'LIST' },
      ],
    }),

    // 开始审核问卷（交易所使用，锁定问卷状态）
    startQuestionnaireReview: builder.mutation<
      {
        success: boolean;
        data: { questionnaire: InitialScreeningQuestionnaire; message: string };
      },
      string
    >({
      query: id => ({
        url: `/questionnaire/${id}/start-review`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Questionnaire', id },
        { type: 'Questionnaire', id: 'LIST' },
      ],
    }),

    // 获取问卷列表
    getQuestionnaires: builder.query<
      {
        success: boolean;
        data: {
          questionnaires: InitialScreeningQuestionnaire[];
          total: number;
          totalPages: number;
          currentPage: number;
          limit: number;
        };
      },
      QuestionnaireFilters & { page?: number; limit?: number }
    >({
      query: params => ({
        url: '/questionnaires',
        method: 'GET',
        params,
      }),
      providesTags: ['Questionnaire'],
    }),

    // 获取问卷详情
    getQuestionnaireById: builder.query<
      {
        success: boolean;
        data: { questionnaire: InitialScreeningQuestionnaire };
      },
      string
    >({
      query: id => `/questionnaire/${id}`,
      providesTags: (result, error, id) => [{ type: 'Questionnaire', id }],
      // 强制每次查询时重新获取数据，避免编辑时使用缓存导致数据不一致
      keepUnusedDataFor: 0,
    }),

    // 根据项目代码获取问卷
    getQuestionnaireByProject: builder.query<
      {
        success: boolean;
        data: { questionnaire: InitialScreeningQuestionnaire };
      },
      string
    >({
      query: projectCode => `/questionnaire/by-project/${projectCode}`,
      providesTags: (result, error, projectCode) => [
        { type: 'Questionnaire', id: projectCode },
      ],
    }),

    // 获取问卷统计数据
    getQuestionnaireStatistics: builder.query<
      {
        success: boolean;
        data: {
          total_received: number;
          total_completed: number;
          today_received: number;
          pending_count: number; // 待审核数量
          under_review_count: number; // 审核中数量
          approved_count: number; // 已通过数量
          rejected_count: number; // 已拒绝数量
          need_supplement_count: number; // 需补充数量
          pass_rate: number;
          average_processing_time: number;
          industry_distribution: Record<string, number>;
          monthly_trend: Array<{
            month: string;
            received: number;
            completed: number;
            pass_rate: number;
          }>;
          reviewer_workload: Array<{
            reviewer_id: string;
            reviewer_name: string;
            processed_count: number;
            avg_time: number;
          }>;
        };
      },
      void
    >({
      query: () => '/questionnaires/statistics',
      providesTags: ['Questionnaire'],
      // 缩短缓存时间，使统计数据更快失效
      keepUnusedDataFor: 0, // 不缓存，确保每次都获取最新数据
    }),

    // 审核问卷 - 增强版API支持项目自动创建
    reviewQuestionnaire: builder.mutation<
      {
        success: boolean;
        data: {
          questionnaire_id: string;
          decision: 'approved' | 'rejected' | 'need_supplement';
          reviewer: {
            id: string;
            name: string;
            role: string;
          };
          reviewed_at: string;
          comments: string;
          message: string;
          project?: {
            project_code: string;
            workflow_id: string;
            current_stage: string;
            created_at: string;
          };
          project_creation_error?: string;
        };
      },
      {
        questionnaireId: string;
        decision: 'approved' | 'rejected' | 'need_supplement';
        comments?: string;
        reasons?: string[];
      }
    >({
      query: ({ questionnaireId, decision, comments, reasons }) => ({
        url: `/questionnaires/${questionnaireId}/review`,
        method: 'POST',
        body: {
          decision,
          comments,
          reasons,
        },
      }),
      // 审核问卷后需要同时失效问卷和项目统计数据
      // 因为审核通过会自动创建项目，影响项目统计
      invalidatesTags: ['Questionnaire', 'ProjectWorkflow'],
    }),

    // 批量操作问卷
    batchOperationQuestionnaires: builder.mutation<
      {
        success: boolean;
        data: {
          processedCount: number;
          successCount: number;
          failedCount: number;
          results: Array<{
            questionnaireId: string;
            success: boolean;
            error?: string;
          }>;
          message: string;
        };
      },
      {
        questionnaireIds: string[];
        action: 'approved' | 'rejected';
        comments?: string;
        reasons?: string[];
      }
    >({
      query: body => ({
        url: '/questionnaires/batch',
        method: 'POST',
        body,
      }),
      // 批量审核问卷也会创建项目，需要同时失效两个标签
      invalidatesTags: ['Questionnaire', 'ProjectWorkflow'],
    }),

    // 导出问卷数据
    exportQuestionnaires: builder.mutation<
      Blob,
      {
        format: 'excel' | 'pdf';
        questionnaireIds?: string[];
      }
    >({
      query: body => ({
        url: '/questionnaires/export',
        method: 'POST',
        body,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    // 获取AI评分
    getQuestionnaireAIScore: builder.mutation<
      { success: boolean; data: { score: number; recommendations: string[] } },
      QuestionnaireRequest
    >({
      query: questionnaireData => ({
        url: '/questionnaire/ai-score',
        method: 'POST',
        body: questionnaireData,
      }),
    }),

    // 问卷自动注册用户
    autoRegisterWithQuestionnaire: builder.mutation<
      {
        success: boolean;
        message: string;
        data: {
          user: User;
          tokens: {
            accessToken: string;
            refreshToken: string;
          };
          questionnaire: {
            id: string;
            projectName?: string;
            status: string;
            submittedAt: string;
          };
          registration: {
            isNewUser: boolean;
            username: string;
            plainPassword?: string; // 只有新用户才有
            loginUrl: string;
          };
        };
      },
      {
        questionnaire: QuestionnaireRequest;
        deviceInfo?: {
          userAgent?: string;
          platform?: string;
          language?: string;
        };
      }
    >({
      query: data => ({
        url: '/questionnaire/auto-register',
        method: 'POST',
        body: data,
      }),
    }),
  }),
});

// 导出hooks
export const {
  useCreateQuestionnaireMutation,
  useSaveQuestionnaireDraftMutation,
  useUpdateQuestionnaireMutation,
  useSubmitQuestionnaireMutation,
  useWithdrawQuestionnaireMutation, // 新增：撤回问卷
  useStartQuestionnaireReviewMutation, // 新增：开始审核（锁定状态）
  useGetQuestionnairesQuery,
  useGetQuestionnaireByIdQuery,
  useGetQuestionnaireByProjectQuery,
  useGetQuestionnaireStatisticsQuery,
  useReviewQuestionnaireMutation,
  useBatchOperationQuestionnairesMutation,
  useExportQuestionnairesMutation,
  useGetQuestionnaireAIScoreMutation,
  useAutoRegisterWithQuestionnaireMutation,
} = questionnaireApi;
